import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthContext";
import styles from "../../Auth/Auth.module.css";
import { verifyOtp } from "../../../services/api";
import { setAuthSession } from "../../../utils/authStorage";

const Login = () => {
  const { login, googleLogin } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    email: "",
    password: "",
    remember: false,
  });
  
  const [targetPhone, setTargetPhone] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpMethods, setOtpMethods] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState("");
  const [showMethodSelection, setShowMethodSelection] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [countdown, setCountdown] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const otpRefs = useRef([]);

  // -------------------------
  // Handlers
  // -------------------------
  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const togglePassword = () => setShowPassword((prev) => !prev);

  const startCountdown = () => {
    setCountdown(60);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleLogin = async (e) => {
    if (e) e.preventDefault();
    if (!form.email || !form.password) {
      toast.error("Email and password required");
      return;
    }
    
    if (selectedMethod === "phone" && !targetPhone && showMethodSelection) {
       toast.error("Please enter a mobile phone number to receive the OTP");
       return;
    }

    setIsSubmitting(true);
    const res = await login(
      form.email,
      form.password,
      selectedMethod || null,
      null,
      selectedMethod === "phone" ? targetPhone : null
    );
    setIsSubmitting(false);

    if (res.ok) {
      if (res.requires2FA) {
        navigate("/login/2fa", { state: { email: res.email } });
        return;
      }

      if (res.requiresVerification) {
        navigate(`/verify-otp/${res.user.email}`, {
          state: { isFirstTime: true },
        });
        return;
      }

      if (res.otpRequired) {
        setOtpMethods(res.methods);
        setShowMethodSelection(true);
        // Default to email if available
        const emailMethod = res.methods.find((m) => m.id === "email");
        if (emailMethod) setSelectedMethod("email");
        else if (res.methods.length > 0) setSelectedMethod(res.methods[0].id);
      } else if (res.otpSent) {
        setOtpSent(true);
        setShowMethodSelection(false);
        startCountdown();
      } else if (res.user) {
        navigate("/user-dashboard");
      }
    }
  };

  const handleSendOtp = async (e) => {
    if (e) e.preventDefault();
    if (!form.email) return toast.error("Enter your email first");
    setIsSubmitting(true);
    const res = await login(
      form.email, 
      form.password, 
      selectedMethod, 
      null, 
      selectedMethod === "phone" ? targetPhone : null
    );
    setIsSubmitting(false);
    if (res.ok && res.otpSent) {
      startCountdown();
    }
  };

  const handleOtpChange = (e, index) => {
    const val = e.target.value.slice(-1);
    if (isNaN(val)) return;
    const newOtp = [...otp];
    newOtp[index] = val;
    setOtp(newOtp);
    if (val && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyOtp = async () => {
    const otpCode = otp.join("");

    if (otpCode.length !== 6 || !/^\d{6}$/.test(otpCode)) {
      toast.error("Please enter a valid 6-digit OTP");
      return;
    }

    setIsSubmitting(true);
    const res = await login(
      form.email, 
      form.password, 
      selectedMethod, 
      otpCode,
      selectedMethod === "phone" ? targetPhone : null
    );
    setIsSubmitting(false);

    if (res.ok && res.user) {
      navigate("/user-dashboard");
    } else if (!res.ok) {
      setOtp(["", "", "", "", "", ""]);
      if (otpRefs.current[0]) {
        otpRefs.current[0].focus();
      }
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    if (!credentialResponse?.credential)
      return toast.error("Google login failed");
    setIsSubmitting(true);
    const res = await googleLogin(credentialResponse.credential);
    setIsSubmitting(false);

    if (res.ok) {
      if (res.requires2FA) {
        navigate("/login/2fa", { state: { email: res.email } });
      } else if (res.requiresVerification) {
        navigate(`/verify-otp/${res.user.email}`, {
          state: { isFirstTime: true },
        });
      } else {
        navigate("/user-dashboard");
      }
    }
  };

  // -------------------------
  // Render
  // -------------------------
  return (
    <div className={styles.authWrapper}>
      <div className={styles.authCard}>
        <h1 className={styles.authTitle}>PixelMoon Login</h1>
        <p className={styles.subtitle}>Welcome back! Sign in to continue.</p>

        <form
          onSubmit={
            otpSent || showMethodSelection
              ? (e) => e.preventDefault()
              : handleLogin
          }
          className={styles.authForm}
        >
          {/* Email & Password (Hidden during OTP/Selection) */}
          {!otpSent && !showMethodSelection && (
            <>
              {/* Email */}
              <div className={styles.formGroup}>
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  className={styles.formInput}
                  required
                  placeholder="Enter your email"
                />
              </div>

              {/* Password */}
              <div className={styles.formGroup}>
                <label>Password</label>
                <div className={styles.passwordWrapper}>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={form.password}
                    onChange={handleChange}
                    className={styles.formInput}
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    className={styles.showBtn}
                    onClick={togglePassword}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Method Selection */}
          {showMethodSelection && (
            <div className={styles.methodSelection}>
              <p className={styles.methodInstructions}>
                Choose how you want to receive your OTP:
              </p>
              <div className={styles.methodList}>
                {otpMethods.map((method) => (
                  <label
                    key={method.id}
                    className={`${styles.methodItem}`}
                  >
                    <input
                      type="radio"
                      name="otpMethod"
                      value={method.id}
                      checked={selectedMethod === method.id}
                      onChange={(e) => {
                         setSelectedMethod(e.target.value);
                         if (e.target.value === "phone" && method.value) {
                             setTargetPhone(method.value);
                         }
                      }}
                    />
                    <div className={styles.methodInfo}>
                      <span className={styles.methodLabel}>{method.label}</span>
                      <span className={styles.methodValue}>
                         {method.id === "email" ? method.value : (method.value || "Not Set")}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              
              {selectedMethod === "phone" && (
                <div className={styles.formGroup} style={{ marginTop: '15px' }}>
                  <label>Enter Mobile Number for OTP</label>
                  <input
                    type="tel"
                    value={targetPhone}
                    onChange={(e) => setTargetPhone(e.target.value)}
                    className={styles.formInput}
                    placeholder="e.g. 9876543210"
                    required
                  />
                </div>
              )}

              <button
                type="button"
                onClick={() => handleLogin()}
                className={styles.authButton}
                disabled={isSubmitting || !selectedMethod}
                style={{ marginTop: '20px' }}
              >
                {isSubmitting ? "Sending..." : "Send OTP"}
              </button>
              <button
                type="button"
                className={styles.backBtn}
                onClick={() => setShowMethodSelection(false)}
              >
                Back to Credentials
              </button>
            </div>
          )}

          {/* OTP */}
          {otpSent && (
            <div className={styles.otpContainer}>
              <p className={styles.otpInstructions}>
                We've sent a 6-digit code to {selectedMethod === "phone" ? targetPhone : form.email}. Please enter it
                below:
              </p>
              <div className={styles.otpInputs}>
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (otpRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(e, index)}
                    onKeyDown={(e) => handleOtpKeyDown(e, index)}
                    className={styles.otpInput}
                    disabled={isSubmitting}
                    aria-label={`Digit ${index + 1} of 6`}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={handleVerifyOtp}
                className={styles.verifyButton}
                disabled={isSubmitting || otp.some((d) => d === "")}
              >
                {isSubmitting ? "Verifying..." : "Verify OTP"}
              </button>
              {countdown > 0 ? (
                <p className={styles.otpResendText}>
                  Resend OTP in {countdown}s
                </p>
              ) : (
                <button
                  type="button"
                  onClick={handleSendOtp}
                  className={styles.otpResendButton}
                  disabled={isSubmitting}
                >
                  Resend OTP
                </button>
              )}
            </div>
          )}

          {/* Remember Me & Forgot Password */}
          {!otpSent && !showMethodSelection && (
            <div className={styles.rememberForgot}>
              <label className={styles.rememberMe}>
                <input
                  type="checkbox"
                  checked={form.remember}
                  onChange={(e) =>
                    setForm({ ...form, remember: e.target.checked })
                  }
                />
                <span>Remember me</span>
              </label>
              <button
                type="button"
                className={styles.forgotPassword}
                onClick={() => navigate("/forgot-password")}
              >
                Forgot password?
              </button>
            </div>
          )}

          {/* Buttons */}
          {!otpSent && !showMethodSelection && (
            <button
              type="submit"
              className={`${styles.authButton} ${
                isSubmitting ? styles.loading : ""
              }`}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Processing..." : "Login"}
            </button>
          )}

          <div className={styles.divider}>OR</div>

          {/* Google Login */}
          <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={() => toast.error("Google login failed")}
              useOneTap
              auto_select
              text="continue_with"
            />
          </GoogleOAuthProvider>

          <div className={styles.authLink}>
            Don't have an account? <Link to="/register">Sign Up</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
